import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDateFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  createLoader,
  parseAsArrayOf,
  parseAsString,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Header } from "@/components/universal/header";
import { ParamDataTableScrollable } from "@/components/ui/param-data-table/param-data-table-scrollable";
import { useGetAuthUser, useGetUsers } from "@/requests/gen/react-query/user";
import { keepPreviousData } from "@tanstack/react-query";
import { UserRoleEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import {
  useGetUserEntityRevenue,
  getGetUserEntityRevenueQueryOptions,
} from "@/requests/gen/react-query/commissions";
import { createFileRoute, type LinkProps } from "@tanstack/react-router";

const commissionsSearchParsers = {
  advisor_ids: parseAsArrayOf(parseAsString),
};

const commissionsSearchAllParsers = {
  ...dataTableSearchParsers,
  ...commissionsSearchParsers,
};

const loadCommissionsSearch = createLoader(commissionsSearchAllParsers);

type CommissionsSearch = inferParserType<typeof commissionsSearchAllParsers>;

// advisor_ids (UI) maps to user_ids (API) — keep that rename in one place.
function commissionsQueryParams(
  p: CommissionsSearch,
): Parameters<typeof getGetUserEntityRevenueQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    entity_ids: p.entity_ids,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    user_ids: p.advisor_ids || [],
  };
}

export const Route = createFileRoute("/_private/_sales-360/commissions/lookup")(
  {
    loader: ({ context: { queryClient }, location }) =>
      queryClient.prefetchQuery(
        getGetUserEntityRevenueQueryOptions(
          commissionsQueryParams(loadCommissionsSearch(location.searchStr)),
        ),
      ),
    component: CommissionsLookupPage,
  },
);

const generateMonthHeaders = (
  created_at_start: string,
  created_at_end: string,
) => {
  const months: string[] = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  let startMonth: number;
  let startYear: number;
  let monthDiff: number;

  if (created_at_start && created_at_end) {
    const fromDate = new Date(created_at_start);
    const toDate = new Date(created_at_end);
    startMonth = fromDate.getMonth();
    startYear = fromDate.getFullYear();
    const endMonth = toDate.getMonth();
    const endYear = toDate.getFullYear();
    const yearDiff = endYear - startYear;
    let adjustedEndMonth = endMonth;
    for (let i = 0; i < yearDiff; i++) {
      adjustedEndMonth += 12;
    }
    monthDiff = adjustedEndMonth - startMonth;
  } else {
    // Default to last 6 months
    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 5); // 5 months back + current month = 6 months
    startMonth = sixMonthsAgo.getMonth();
    startYear = sixMonthsAgo.getFullYear();
    monthDiff = 5; // 6 months total (0-5 inclusive)
  }

  const filteredMonths = [];
  for (let i = 0; i < monthDiff + 1; i++) {
    const monthIndex = (startMonth + i) % 12;
    const year = startYear + Math.floor((startMonth + i) / 12);
    filteredMonths.push({
      month: months[monthIndex],
      year: year,
    });
  }

  return filteredMonths.map((item, index) => ({
    key: (startMonth + index + 1).toString(),
    label: `${item.month} ${item.year}`,
  }));
};

// Define the columns for the table
interface CommissionTableProps {
  link?: LinkProps;
  advisor: string;
  practiceName: string;
  // Dynamic month columns will be added later
  [monthKey: string]: string | number | LinkProps | undefined;
}

// Main component
function CommissionsLookupPage() {
  const user = useGetAuthUser();
  const [queryParams, setQueryParams] = useQueryStates(
    commissionsSearchAllParsers,
  );

  const users = useGetUsers(
    {
      page: queryParams.page,
      limit: queryParams.limit,
      search: queryParams.search,
      ascending: queryParams.ascending,
      entity_ids: queryParams.entity_ids,
      roles: [UserRoleEnum.sales_advisor], // Filter for only sales advisors
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const monthHeaders = generateMonthHeaders(
    queryParams.created_at_start || "",
    queryParams.created_at_end || new Date().toISOString(),
  );

  const revenue_data = useGetUserEntityRevenue(
    commissionsQueryParams(queryParams),
  );

  const tableData: CommissionTableProps[] =
    revenue_data.data
      ?.map((item) => {
        const monthData: { [key: string]: string } = {};
        monthHeaders.forEach((month) => {
          if (!item.month || item.month.toString() !== month.key) {
            monthData[`${month.key}`] = "--";
            return;
          }
          monthData[`${month.key}`] = item.month
            ? `$${item.revenue_amount.toFixed(2)}`
            : "";
        });

        const advisorUser = users.data?.items.find(
          (user) => user.user_id === item.user_id,
        );
        if (!advisorUser) {
          return null;
        }
        return {
          advisor: advisorUser
            ? `${advisorUser.first_name} ${advisorUser.last_name}`
            : "Unknown Advisor",
          practiceName: item.entity_name,
          ...monthData,
        };
      })
      .filter((item): item is CommissionTableProps => item !== null) || [];

  return (
    <div className="text-left">
      <Header
        crumbs={[{ name: "Commissions", link: { to: "/commissions/lookup" } }]}
      />
      <Card className="w-full max-w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Commissions Report</CardTitle>
          <CardDescription>
            Monthly commission report for all advisors.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={revenue_data.isFetching} />
            {user.data?.role === "admin" && (
              <DropdownFilter
                title="Advisor"
                options={[
                  {
                    items:
                      users.data?.items.map((user) => ({
                        label: `${user.first_name} ${user.last_name}`,
                        value: user.user_id,
                      })) ?? [],
                  },
                ]}
                values={queryParams.advisor_ids || []}
                onChange={(values) => setQueryParams({ advisor_ids: values })}
              />
            )}
            <DropdownFilter
              title="Entity"
              options={[
                {
                  items:
                    users.data?.items
                      .flatMap((user) =>
                        user.user_entities.map((ue) => ue.entity),
                      )
                      .filter(
                        (entity, index, self) =>
                          index ===
                          self.findIndex(
                            (e) => e.entity_id === entity.entity_id,
                          ),
                      )
                      .map((entity) => ({
                        label: entity.name,
                        value: entity.entity_id,
                      })) || [],
                },
              ]}
              values={queryParams.entity_ids}
              onChange={(values) => setQueryParams({ entity_ids: values })}
            />
            <ParamDateFilter title="Date Range" name="created_at" />
            <ParamClearButton />
          </ParamDataTableToolbar>

          {/* Table with fixed columns and horizontal scroll */}
          <ParamDataTableScrollable
            columns={[
              {
                accessorKey: "advisor",
                header: "Advisor",
                cell: (info) => info.getValue(),
                size: 150,
              },
              {
                accessorKey: "practiceName",
                header: "Practice Name",
                cell: (info) => info.getValue(),
                size: 200,
              },
              // Dynamically add month columns
              ...monthHeaders.map((month) => ({
                accessorKey: `${month.key}`,
                header: month.label,
                size: 120,
              })),
            ]}
            data={tableData}
            isLoading={false}
            isError={false}
            fixedColumnsCount={2} // Fix the first 3 columns
          />
        </CardContent>
      </Card>
    </div>
  );
}
